import { t } from 'i18next';

import { errorToast } from '../toast';

export interface PopupWaitFor<T> {
  result: Promise<T>;
  cleanup: () => void;
}

const POPUP_CALLBACK_CHANNEL_KEY = 'popup-callback-channel';
const POPUP_CALLBACK_ACKNOWLEDGED = 'popup-callback-acknowledged';

export function openPopupWindow(url?: string | URL, target?: string, features?: string) {
  const popup = window.open(url, target, features);
  if (!popup) {
    errorToast(t('popupWindow.blockedMsg', { ns: 'ui' }));
    return null;
  }

  let callbackChannelName: string | null = `popup-callback-${crypto.randomUUID()}`;
  try {
    popup.sessionStorage.setItem(POPUP_CALLBACK_CHANNEL_KEY, callbackChannelName);
  } catch {
    // A direct cross-origin URL may finish loading before its session storage is accessible.
    callbackChannelName = null;
  }

  const submitFormWithHiddenInput = (submitUrl: string | URL, fields: Record<string, string>) => {
    const form = popup.document.createElement('form');
    form.setAttribute('action', submitUrl.toString());
    form.setAttribute('method', 'POST');
    popup.document.body.append(form);

    for (const [name, value] of Object.entries(fields)) {
      const input = popup.document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', name);
      input.setAttribute('value', value);
      form.appendChild(input);
    }

    form.submit();
  };

  /**
   * Utility function to wait for the first of several possible popup close events, then clean up
   * any resources allocated by each method.
   */
  const waitForFirst = async <T>(...waits: PopupWaitFor<T>[]) => {
    try {
      return await Promise.race(waits.map((wait) => wait.result));
    } finally {
      for (const wait of waits) {
        wait.cleanup();
      }
    }
  };

  const waitForWindowClose = (): PopupWaitFor<null> => {
    let intervalId: NodeJS.Timeout;
    let coopIsolationDetected = false;
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      clearInterval(intervalId);
    };

    const result = new Promise<null>((resolve) => {
      intervalId = setInterval(() => {
        if (!popup.closed) {
          return;
        }
        if (!document.hasFocus()) {
          coopIsolationDetected = true;
          return;
        }
        if (!coopIsolationDetected) {
          resolve(null);
        }
      }, 500);
    });

    return {
      result,
      cleanup,
    };
  };

  /**
   * If the popup was redirected to the integrations auth callback path, the page will send
   * a message back with the final URL. BroadcastChannel remains available when a provider's
   * Cross-Origin-Opener-Policy severs window.opener.
   */
  const waitForUrlMessage = (expectedOrigin: string): PopupWaitFor<URL> => {
    let messageHandler: (event: MessageEvent) => void;
    let broadcastChannel: BroadcastChannel | null = null;
    let settled = false;
    let cleaned = false;

    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      window.removeEventListener('message', messageHandler);
      broadcastChannel?.close();
    };

    const result = new Promise<URL>((resolve, reject) => {
      const deliver = (value: string) => {
        if (settled) return;
        settled = true;
        popup.close();
        try {
          resolve(new URL(value));
        } catch {
          reject(new TypeError('Popup callback URL is invalid'));
        }
      };
      messageHandler = (event) => {
        if (
          event.source !== popup ||
          event.origin !== expectedOrigin ||
          typeof event.data !== 'string'
        ) {
          return;
        }
        deliver(event.data);
      };
      window.addEventListener('message', messageHandler);

      if (callbackChannelName && typeof BroadcastChannel !== 'undefined') {
        broadcastChannel = new BroadcastChannel(callbackChannelName);
        broadcastChannel.addEventListener('message', (event) => {
          if (typeof event.data !== 'string') return;
          broadcastChannel?.postMessage(POPUP_CALLBACK_ACKNOWLEDGED);
          deliver(event.data);
        });
      }
    });

    return {
      result,
      cleanup,
    };
  };

  return {
    window: popup,
    submitFormWithHiddenInput,
    waitForFirst,
    waitForWindowClose,
    waitForUrlMessage,
  };
}

export function notifyPopupWindowOpener(url: string | URL): void {
  const callbackUrl = url.toString();

  const opener = window.opener as Window | null;
  if (opener && opener !== window) {
    opener.postMessage(callbackUrl, window.location.origin);
  }

  const callbackChannelName = window.sessionStorage.getItem(POPUP_CALLBACK_CHANNEL_KEY);
  if (callbackChannelName && typeof BroadcastChannel !== 'undefined') {
    const broadcastChannel = new BroadcastChannel(callbackChannelName);
    broadcastChannel.addEventListener('message', (event) => {
      if (event.data === POPUP_CALLBACK_ACKNOWLEDGED) {
        broadcastChannel.close();
        window.close();
      }
    });
    broadcastChannel.postMessage(callbackUrl);
  }
}
